/**
 * Scoped URL helpers.
 *
 * The portal's customer-scoped pages live under hierarchical URLs:
 *
 *   super_admin  →  /reseller/{rN}/customer/{cN}/account
 *   reseller_owner / agent  →  /customer/{cN}/account
 *   customer roles (office_manager etc.) →  /customer/{cN}/account
 *
 * Where rN / cN are the four-digit `reseller_number` / `customer_number`
 * fields (NOT the UUIDs). These are stable, short, human-readable, and
 * already shown in the portal everywhere via `formatRef`.
 *
 * This module owns the URL shape so every component, every Link, and
 * every navigate() call produces the same form. Use `buildScopedPath`
 * for relative paths ("/recordings/abc") and `extractScopeFromPath`
 * (rare) to reverse one out of a current location.
 */

export interface ScopeNumbers {
  /** Reseller number (4-digit) — required for super_admin scoped views. */
  resellerNumber?: number | null;
  /** Customer number (4-digit) — present for all customer-scoped views. */
  customerNumber?: number | null;
}

/**
 * Build a scoped URL by prefixing the right `/reseller/{rN}/customer/{cN}`
 * (or `/customer/{cN}`) to the relative path.
 *
 *   buildScopedPath('/account', { customerNumber: 1001 })
 *     → '/customer/1001/account'
 *
 *   buildScopedPath('/account', { resellerNumber: 1, customerNumber: 1001 })
 *     → '/reseller/1/customer/1001/account'
 *
 *   buildScopedPath('/account', {})
 *     → '/account'   (no scope = "my user" view, only valid for some pages)
 *
 * The path argument MUST start with a leading '/'. We strip the slash
 * before joining so callers don't accidentally produce '//' segments.
 */
export function buildScopedPath(path: string, scope: ScopeNumbers): string {
  const clean = path.startsWith('/') ? path : `/${path}`;
  const segments: string[] = [];
  if (scope.resellerNumber != null) segments.push(`reseller/${scope.resellerNumber}`);
  if (scope.customerNumber != null) segments.push(`customer/${scope.customerNumber}`);
  if (segments.length === 0) return clean;
  return `/${segments.join('/')}${clean}`;
}

/**
 * Try to extract scope numbers from a path string. Returns whatever
 * portion of the scope is encoded in the URL — useful for converting
 * an OLD flat URL into a NEW scoped one given the user's current
 * Zustand context.
 *
 *   extractScopeFromPath('/reseller/1/customer/1001/account')
 *     → { resellerNumber: 1, customerNumber: 1001, tail: '/account' }
 *
 *   extractScopeFromPath('/customer/1001/recordings/abc')
 *     → { customerNumber: 1001, tail: '/recordings/abc' }
 *
 *   extractScopeFromPath('/recordings')
 *     → { tail: '/recordings' }
 */
export function extractScopeFromPath(
  path: string,
): { resellerNumber?: number; customerNumber?: number; tail: string } {
  const m = /^\/(?:reseller\/(\d+)\/)?(?:customer\/(\d+))?(\/.*)?$/.exec(path);
  if (!m) return { tail: path };
  const out: { resellerNumber?: number; customerNumber?: number; tail: string } = {
    tail: m[3] || '/',
  };
  if (m[1]) out.resellerNumber = Number(m[1]);
  if (m[2]) out.customerNumber = Number(m[2]);
  return out;
}

/**
 * The set of OLD flat URL prefixes that should auto-redirect to their
 * scoped form when the user has a customer context set. Anything not
 * in this list stays as-is (e.g. `/customers`, `/resellers`,
 * `/dids`, `/carriers` are reseller-level / cross-tenant and stay flat).
 *
 * Keep in sync with the route definitions in `app/router.tsx`.
 */
export const CUSTOMER_SCOPED_PATHS: ReadonlyArray<RegExp> = [
  /^\/account($|\/)/,
  /^\/recordings($|\/)/,
  /^\/folders($|\/)/,
  /^\/voicemails($|\/)/,
  /^\/conference-rooms($|\/)/,
  /^\/conferences\/sessions\/[^/]+($|\/)/,
  /^\/conferences\/[^/]+($|\/)/,
  /^\/extensions($|\/)/,
  /^\/ring-groups($|\/)/,
  /^\/ivr\/trees($|\/)/,
  /^\/call-log($|\/)/,
  /^\/caller-bookmarks($|\/)/,
  /^\/caller-profiles($|\/)/,
  /^\/sms($|\/)/,
  /^\/points($|\/)/,
  /^\/surveys($|\/)/,
  /^\/quizzes($|\/)/,
  /^\/courses($|\/)/,
  /^\/products($|\/)/,
  /^\/orders($|\/)/,
  /^\/forum($|\/)/,
  /^\/auth-lists($|\/)/,
  /^\/time-frames($|\/)/,
  /^\/contacts($|\/)/,
  /^\/petitions($|\/)/,
  /^\/analytics\/ivr($|\/)/,
  /^\/settings\/audit-log($|\/)/,
];

/** True when `path` matches any pattern in `CUSTOMER_SCOPED_PATHS`. */
export function isCustomerScopedPath(path: string): boolean {
  return CUSTOMER_SCOPED_PATHS.some((re) => re.test(path));
}
